import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { Vendor } from "../types";
import { updateVendor, updateVendorValue, uploadLogo } from "../api";
import NotificationService from "../components/notification/NotificationService";
import { useAppDispatch, useAppSelector } from "../hooks";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { getGuid } from "../utils";
import imageCompression from "browser-image-compression";

interface Props {
  initialVendor: Vendor;
  vendors: Vendor[];
  returnCallback?: () => void;
  updatedVendors?: (vendors: Vendor[]) => void;
}

interface City {
  nr: string;
  navn: string;
}

const VendorEditForm: React.FC<Props> = ({
  initialVendor,
  returnCallback,
  updatedVendors,
  vendors,
}) => {
  const [logoLoading, setLogoLoading] = useState<boolean>(false);
  const [vendor, setVendor] = useState<Vendor>(initialVendor);
  const [zip, setZip] = useState("");
  const [allCities, setAllCities] = useState<City[]>([]);
  const [linkError, setLinkError] = useState("");
  const [instagramError, setInstagramError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch all cities once
  useEffect(() => {
    const fetchAllCities = async () => {
      try {
        const res = await fetch(
          `https://api.dataforsyningen.dk/postnumre?landpostnumre=true`
        );
        const data = await res.json();
        setAllCities(data.sort((a: City, b: City) => a.nr.localeCompare(b.nr)));
      } catch (error) {
        console.error("Failed to fetch cities", error);
      }
    };
    fetchAllCities();
  }, []);

  // Update zip if vendor already has a located city
  useEffect(() => {
    const found = allCities.find((c) => c.navn === vendor.located);
    setZip(found?.nr || "");
  }, [vendor.located, allCities]);

  const handleChange =
    (field: keyof Vendor) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setVendor({ ...vendor, [field]: event.target.value });
    };

  const handleZipChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newZip = event.target.value;
    setZip(newZip);

    const city = allCities.find((c) => c.nr === newZip)?.navn || "";
    setVendor({ ...vendor, located: city });
  };

  const handleLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setVendor({ ...vendor, link: value });

    if (value && !/^https?:\/\//i.test(value)) {
      setLinkError("Linket skal starte med http:// eller https://");
    } else {
      setLinkError("");
    }
  };

  const handleInstagramChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setVendor({ ...vendor, instagram: value });

    if (value && !/^https:\/\/www\.instagram\.com\//i.test(value)) {
      setInstagramError(
        "Instagram-linket skal starte med https://www.instagram.com/"
      );
    } else {
      setInstagramError("");
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setVendor({ ...vendor, email: value });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value && !emailRegex.test(value)) {
      setEmailError("Indtast en gyldig emailadresse");
    } else {
      setEmailError("");
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setVendor({ ...vendor, phone: value });

    // Telefon validering: kun tal, mellemrum, bindestreger og plus
    const phoneRegex = /^[0-9 +\-]*$/;
    if (value && !phoneRegex.test(value)) {
      setPhoneError("Telefonnummer kan kun indeholde tal, mellemrum, + og -");
    } else {
      setPhoneError("");
    }
  };

  const handleSave = () => {
    const body = {
      ...vendor,
      contact: vendor.contact.id,
      type: vendor.type?.id,
      mainVendor: vendor.mainVendor?.id,
    };
    updateVendor(body)
      .then(() => {
        const newData = vendors.map((v) => {
          if (v.id === vendor.id) {
            return {
              ...vendor,
              contact: vendor.contact,
              type: vendor.type,
              mainVendor: vendor.mainVendor,
            };
          } else {
            return v;
          }
        });

        updatedVendors && updatedVendors(newData);

        NotificationService.showToast({
          text: "Dine oplysninger blev succesfuldt opdateret",
          severity: "success",
        });
        returnCallback && returnCallback();
      })
      .catch(() => {
        NotificationService.showToast({
          text: "Der skete en fejl. Data kunne ikke gemmes.",
          severity: "error",
        });
      });
  };

  const getDisabledReason = () => {
    if (!vendor.mainVendor) {
      if (!vendor.name) return "Navn er påkrævet";
      if (!vendor.located) return "Postnummer er påkrævet";
      if (linkError) return linkError;
      if (instagramError) return instagramError;
      if (emailError) return emailError;
      if (phoneError) return phoneError;
      return "";
    }
  };

  const uploadLogoHandler = (file: File | null) => {
    if (!file || !vendor?.id) return;
    uploadLogo(file, getGuid(), "vendors")
      .then((url) => {
        vendor.id &&
          setVendor((prev) => (prev ? { ...prev, logo: url } : prev));
      })
      .catch((e) =>
        NotificationService.showToast({
          text: e.message,
          severity: "error",
        })
      )
      .finally(() => {
        setLogoLoading(false);
      });
  };

  const compressImage = async (file: File) => {
    setLogoLoading(true);
    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.1,
        useWebWorker: true,
      });
      uploadLogoHandler(compressedFile);
    } catch (error) {
      NotificationService.showToast({
        text: "Upload failed",
        severity: "error",
      });
      console.error("Image compression failed:", error);
      throw error;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#faead7" }}>
      <IconButton
        sx={{
          margin: 4,
          backgroundColor: "#715939",
          marginRight: "10px",
          color: "#faead7",
          "&:hover": { backgroundColor: "#c09c72" },
        }}
        aria-label="back"
        onClick={returnCallback}
        size="large"
      >
        <ChevronLeftIcon />
      </IconButton>
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            backgroundColor: "#faead7",
            color: "#191509",
            borderRadius: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: "#715939", marginBottom: "30px" }}
          >
            Rediger virksomhedens oplysninger
          </Typography>
          {vendor.mainVendor && (
            <Typography
              variant="body2"
              sx={{ color: "#715939", marginBottom: "30px" }}
            >
              Kun beskrivelse kan ændres for denne virksomhed. Resterende
              oplysninger hentes fra virksomheden{" "}
              <span style={{ fontWeight: 700 }}>{vendor.mainVendor?.name}</span>
              , der er tilknyttet kategorien{" "}
              <span style={{ fontWeight: 700 }}>
                {vendor.mainVendor?.type?.name}.
              </span>
            </Typography>
          )}

          <Grid container spacing={3}>
            {!vendor.mainVendor && (
              <>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Box mt={2} mb={2} display="flex" justifyContent="center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg, image/png"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        e.target.files && compressImage(e.target.files[0])
                      }
                    />
                    <Avatar
                      src={vendor.logo}
                      sx={{
                        width: 100,
                        height: 100,
                        mt: 1,
                        cursor: "pointer",
                        color: "#715939",
                        bgcolor: "transparent",
                        fontSize: 12,
                        fontWeight: "bold",
                        border: !vendor.logo ? "1px solid #715939" : "none",
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {logoLoading ? (
                        <CircularProgress size={20} sx={{ color: "#715939" }} />
                      ) : (
                        !vendor.logo && "Logo"
                      )}
                    </Avatar>
                  </Box>
                </div>
                <Grid item xs={12}>
                  <TextField
                    label="Navn"
                    fullWidth
                    required
                    value={vendor.name}
                    onChange={handleChange("name")}
                    sx={{
                      "& .MuiInputBase-input": { color: "#191509" },
                      "& .MuiInputLabel-root": { color: "#8b6d46" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#c09c72" },
                        "&:hover fieldset": { borderColor: "#715939" },
                      },
                    }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                label="Beskrivelse"
                fullWidth
                multiline
                minRows={3}
                value={vendor.description || ""}
                onChange={handleChange("description")}
                inputProps={{ maxLength: 200 }}
                helperText={`${(vendor.description || "").length}/200`}
                sx={{
                  "& .MuiInputBase-input": { color: "#191509" },
                  "& .MuiInputLabel-root": { color: "#8b6d46" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#c09c72" },
                    "&:hover fieldset": { borderColor: "#715939" },
                  },
                }}
              />
            </Grid>

            {/* Postnummer og By */}
            {!vendor.mainVendor && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Postnummer"
                    fullWidth
                    value={zip}
                    onChange={handleZipChange}
                    required
                    sx={{
                      "& .MuiInputBase-input": { color: "#191509" },
                      "& .MuiInputLabel-root": { color: "#8b6d46" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#c09c72" },
                        "&:hover fieldset": { borderColor: "#715939" },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="By"
                    fullWidth
                    value={vendor.located || ""}
                    disabled
                    sx={{
                      "& .MuiInputBase-input": { color: "#191509" },
                      "& .MuiInputLabel-root": { color: "#8b6d46" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#c09c72" },
                      },
                    }}
                  />
                </Grid>

                {/* Telefonnummer og Email */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Telefonnummer"
                    fullWidth
                    value={vendor.phone || ""}
                    onChange={handlePhoneChange}
                    error={!!phoneError}
                    helperText={phoneError}
                    sx={{
                      "& .MuiInputBase-input": { color: "#191509" },
                      "& .MuiInputLabel-root": { color: "#8b6d46" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#c09c72" },
                        "&:hover fieldset": { borderColor: "#715939" },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={vendor.email || ""}
                    onChange={handleEmailChange}
                    error={!!emailError}
                    helperText={emailError}
                    sx={{
                      "& .MuiInputBase-input": { color: "#191509" },
                      "& .MuiInputLabel-root": { color: "#8b6d46" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#c09c72" },
                        "&:hover fieldset": { borderColor: "#715939" },
                      },
                    }}
                  />
                </Grid>

                {/* Hjemmeside og Instagram */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Hjemmeside"
                    fullWidth
                    value={vendor.link || ""}
                    onChange={handleLinkChange}
                    error={!!linkError}
                    helperText={linkError}
                    sx={{
                      "& .MuiInputBase-input": { color: "#191509" },
                      "& .MuiInputLabel-root": { color: "#8b6d46" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#c09c72" },
                        "&:hover fieldset": { borderColor: "#715939" },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Instagram link"
                    fullWidth
                    helperText={
                      instagramError ||
                      "https://www.instagram.com/instagram-username"
                    }
                    value={vendor.instagram || ""}
                    onChange={handleInstagramChange}
                    error={!!instagramError}
                    sx={{
                      "& .MuiInputBase-input": { color: "#191509" },
                      "& .MuiInputLabel-root": { color: "#8b6d46" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#c09c72" },
                        "&:hover fieldset": { borderColor: "#715939" },
                      },
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Gem-knap og offentliggør */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={vendor.visible}
                      onChange={(event) =>
                        setVendor({ ...vendor, visible: event.target.checked })
                      }
                      sx={{
                        color: "#715939",
                        "&.Mui-checked": { color: "#c09c72" },
                      }}
                    />
                  }
                  label="Synlig på hjemmesiden"
                />
                <Tooltip
                  title={getDisabledReason()}
                  arrow
                  disableHoverListener={!getDisabledReason()}
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSave}
                      size="large"
                      disabled={!!getDisabledReason()}
                      sx={{
                        backgroundColor: "#715939",
                        color: "#faead7",
                        "&:hover": { backgroundColor: "#c09c72" },
                      }}
                    >
                      Gem
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default VendorEditForm;
